import {Col, Form, Input, InputRef, message, Row, Tag, Tooltip} from 'antd';
import {FormInstance} from 'antd/lib/form';
import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import _ from "lodash";
import Search from "antd/lib/input/Search";
import {SearchProps} from "antd/lib/input";

const FONT_SIZE = 16
const tagInputStyle: React.CSSProperties = {
    width: 150,
    marginInlineEnd: 8,
    verticalAlign: 'top',
    fontSize: FONT_SIZE
};

interface Props {
    form: FormInstance
    fieldId: string
    fieldName: string // label in UI
}

const LikeFilter = ({
                        form,
                        fieldId,
                        fieldName,
                    }: Props) => {
    const [inputValue, setInputValue] = useState('');
    const [editInputIndex, setEditInputIndex] = useState(-1);
    const [editInputValue, setEditInputValue] = useState('');
    const editInputRef = useRef<InputRef>(null);

    useEffect(() => {
        const data: string | undefined = form.getFieldValue(fieldId)
        if (data && !_.isArray(data)) {
            const values = data.split(',');
            setTags(values)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        editInputRef.current?.focus();
    }, [editInputValue]);

    const getTags = (): string[] => {
        return form.getFieldValue(fieldId) || []
    }

    const setTags = (newTags: string[]) => {
        form.setFieldValue(fieldId, newTags)
        form.setFieldValue(`${fieldId}_labels`, newTags)
    };

    const handleRemove = (removedTag: string) => {
        const tags = getTags()
        const newTags = tags.filter((tag) => tag !== removedTag);
        setTags([...newTags]);
    };

    const handleEditInputConfirm = () => {
        const tags = getTags()
        const newTags = [...tags];
        newTags[editInputIndex] = editInputValue;
        setTags(newTags);
        setEditInputIndex(-1);
        setEditInputValue('');
    };

    const onSearch: SearchProps['onSearch'] = (value: string, event?: ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLInputElement>) => {
        const tags = getTags()
        const specialCharRegex = new RegExp("^[a-zA-Z0-9()@.' ,-]*$");
        if (specialCharRegex.test(inputValue)) {
            if (inputValue && !tags.includes(inputValue)) {
                setTags([...tags, inputValue]);
            } else {
                message.warn(`${inputValue} is a duplicate.`);
            }
            setInputValue('')
        } else {
            event!.preventDefault();
            message.warn(`${inputValue} has invalid character(s).`);
        }
    }

    return (
        <>
            <Row>
                <Col span={24}>
                    <div style={{padding: '5px'}}>
                        Enter a search term to filter {fieldName}. Only data matching your search term will be shown.
                    </div>
                    <div style={{padding: '5px'}}>
                        You can add multiple terms (using OR logic).<br/>
                        Double click on a term to edit or click on 'X' to remove.<br/>
                        Valid characters are alphanumeric, space and <code>()@.',-</code>.
                    </div>
                </Col>
            </Row>
            <div style={{margin: '5px 0 0 0'}}>
                <Search
                    placeholder="Input search term"
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onSearch={onSearch}
                    enterButton
                />
            </div>
            <br/>
            {getTags()
                .map<React.ReactNode>((tag, index) => {
                    if (editInputIndex === index) {
                        return (
                            <Input
                                ref={editInputRef}
                                key={tag}
                                style={tagInputStyle}
                                value={editInputValue}
                                onChange={e => setEditInputValue(e.target.value)}
                                onBlur={handleEditInputConfirm}
                                onPressEnter={handleEditInputConfirm}
                                data-testid="tag-edit"
                            />
                        );
                    }
                    const isLongTag = tag.length > 20;
                    const tagElem = (
                        <Tag
                            key={tag}
                            closable={true}
                            style={{userSelect: 'none', margin: '4px', padding: '2px 2px 2px 6px', fontSize: FONT_SIZE}}
                            onClose={() => handleRemove(tag)}
                        >
            <span
                onDoubleClick={(e) => {
                    setEditInputIndex(index);
                    setEditInputValue(tag);
                    e.preventDefault();
                }}
            >
              {isLongTag ? `${tag.slice(0, 20)}...` : tag}
            </span>
                        </Tag>
                    );
                    return isLongTag ? (
                        <Tooltip title={tag} key={tag}>
                            {tagElem}
                        </Tooltip>
                    ) : (
                        tagElem
                    );
                })}
            <Form.Item name={fieldId} hidden={true}>
                <Input/>
            </Form.Item>
            <Form.Item name={`${fieldId}_labels`} hidden={true}>
                <Input/>
            </Form.Item>
        </>
    );

}

export default LikeFilter